/**
 * VIRAJ Forensic UI Components v2.0
 * Drop-in replacements for AnalyzePage.jsx
 *
 * Changes from v1.0:
 *   - TriggerHypothesis: disclaimer removed, corpus_note shown, ranked % added
 *   - WhatToCheck: new component — ordered action steps per archetype
 *   - ForensicNarrative: section label changed to "What happened"
 *   - ConfidenceBadge: corpus_note shown below badge
 *   - All internal engine language removed
 */

import { useState } from "react";

const MONO = "var(--font-mono)";

// ─────────────────────────────────────────────────────────────
// ArchetypeBadge
// ─────────────────────────────────────────────────────────────

export function ArchetypeBadge({ archetype }) {
  if (!archetype || archetype.id === "insufficient_data") return null;

  const colorMap = {
    cycle_failure:      { bg: "rgba(239,68,68,0.12)",   border: "rgba(239,68,68,0.3)",   text: "#FCA5A5" },
    sudden_collapse:    { bg: "rgba(249,115,22,0.12)",  border: "rgba(249,115,22,0.3)",  text: "#FDBA74" },
    silent_degradation: { bg: "rgba(234,179,8,0.12)",   border: "rgba(234,179,8,0.3)",   text: "#FDE047" },
    spend_spike:        { bg: "rgba(168,85,247,0.12)",  border: "rgba(168,85,247,0.3)",  text: "#D8B4FE" },
    attribution_delay:  { bg: "rgba(59,130,246,0.12)",  border: "rgba(59,130,246,0.3)",  text: "#93C5FD" },
  };
  const c = colorMap[archetype.id] ?? { bg: "var(--bg-elevated)", border: "var(--border-subtle)", text: "var(--text-secondary)" };

  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 10px", borderRadius: "var(--radius-sm)",
      background: c.bg, border: `1px solid ${c.border}`,
      fontFamily: MONO, fontSize: 9, fontWeight: 700,
      color: c.text, letterSpacing: "0.10em", textTransform: "uppercase",
    }}>
      {archetype.label}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// ConfidenceBadge — with corpus_note
// ─────────────────────────────────────────────────────────────

export function ConfidenceBadge({ confidence_output }) {
  if (!confidence_output) return null;

  const colorMap = {
    high:   { text: "#4ADE80", border: "rgba(74,222,128,0.3)" },
    medium: { text: "#FDE047", border: "rgba(253,224,71,0.3)" },
    low:    { text: "#FDBA74", border: "rgba(253,186,116,0.3)" },
  };
  const c = colorMap[confidence_output.level] ?? colorMap.low;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "3px 10px", borderRadius: "var(--radius-sm)",
        border: `1px solid ${c.border}`,
        fontFamily: MONO, fontSize: 9, fontWeight: 700,
        color: c.text, letterSpacing: "0.10em", textTransform: "uppercase",
        width: "fit-content",
      }}>
        {confidence_output.label}
      </div>
      {confidence_output.corpus_note && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: "var(--text-muted)", lineHeight: 1.5 }}>
          {confidence_output.corpus_note}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// ForensicNarrative — "What happened" in buyer language
// ─────────────────────────────────────────────────────────────

export function ForensicNarrative({ forensic }) {
  if (!forensic?.narrative_forensic) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{
        fontFamily: MONO, fontSize: 9, letterSpacing: "0.14em",
        textTransform: "uppercase", color: "var(--text-muted)",
      }}>
        What happened
      </div>
      <div style={{
        fontFamily: MONO, fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.75,
      }}>
        {forensic.narrative_forensic}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// TriggerHypothesis v2.0
//
// - No disclaimer
// - corpus_note shown prominently
// - Primary cause shown with percentage
// - Alternatives ranked by percentage, collapsed by default
// ─────────────────────────────────────────────────────────────

export function TriggerHypothesis({ trigger_hypothesis }) {
  const [expanded, setExpanded] = useState(false);
  if (!trigger_hypothesis?.primary) return null;

  const { corpus_note, primary, alternatives } = trigger_hypothesis;
  const hasAlts = alternatives?.length > 0;

  return (
    <div style={{
      padding: "16px 18px", borderRadius: "var(--radius-sm)",
      border: "1px solid var(--border-subtle)", background: "var(--bg-elevated)",
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{
        fontFamily: MONO, fontSize: 9, letterSpacing: "0.14em",
        textTransform: "uppercase", color: "var(--text-muted)",
      }}>
        Most likely cause
      </div>

      {/* Corpus note */}
      {corpus_note && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: "var(--text-muted)", lineHeight: 1.5 }}>
          {corpus_note}
        </div>
      )}

      {/* Primary cause */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        {primary.pct != null && (
          <div style={{
            fontFamily: MONO, fontSize: 13, fontWeight: 700,
            color: "var(--accent-primary)", flexShrink: 0,
          }}>
            {primary.pct}%
          </div>
        )}
        <div style={{ fontFamily: MONO, fontSize: 12, color: "var(--text-primary)", lineHeight: 1.6 }}>
          {primary.text}
        </div>
      </div>

      {/* Alternatives toggle */}
      {hasAlts && (
        <>
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              fontFamily: MONO, fontSize: 10, color: "var(--text-muted)",
              background: "none", border: "none", padding: 0,
              cursor: "pointer", textAlign: "left",
              textDecoration: "underline",
            }}
          >
            {expanded ? "Hide other possibilities" : `Show ${alternatives.length} other possible cause${alternatives.length !== 1 ? "s" : ""}`}
          </button>

          {expanded && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 4 }}>
              {alternatives.map((alt, i) => (
                <div key={i} style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                  {alt.pct != null && (
                    <div style={{
                      fontFamily: MONO, fontSize: 11, fontWeight: 700,
                      color: "var(--text-muted)", flexShrink: 0, minWidth: 28,
                    }}>
                      {alt.pct}%
                    </div>
                  )}
                  <div style={{ fontFamily: MONO, fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.6 }}>
                    {alt.text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// WhatToCheck — new component
//
// Shows ordered action steps from forensic.what_to_check.
// Buyer-facing. No engine language.
// ─────────────────────────────────────────────────────────────

export function WhatToCheck({ what_to_check }) {
  if (!what_to_check?.steps?.length) return null;

  return (
    <div style={{
      padding: "16px 18px", borderRadius: "var(--radius-sm)",
      border: "1px solid var(--border-subtle)", background: "var(--bg-elevated)",
      display: "flex", flexDirection: "column", gap: 14,
    }}>
      <div style={{
        fontFamily: MONO, fontSize: 9, letterSpacing: "0.14em",
        textTransform: "uppercase", color: "var(--text-muted)",
      }}>
        Where to look first
      </div>

      {what_to_check.intro && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: "var(--text-muted)" }}>
          {what_to_check.intro}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {what_to_check.steps.map((step, i) => (
          <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            {/* Step number */}
            <div style={{
              flexShrink: 0, width: 22, height: 22,
              borderRadius: "50%",
              background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: MONO, fontSize: 10, fontWeight: 700,
              color: "var(--text-secondary)",
            }}>
              {i + 1}
            </div>
            {/* Step text */}
            <div style={{
              fontFamily: MONO, fontSize: 12, color: "var(--text-secondary)",
              lineHeight: 1.7, paddingTop: 2,
            }}>
              {step}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// TimelineAnchors
// ─────────────────────────────────────────────────────────────

export function TimelineAnchors({ timeline_anchors }) {
  if (!timeline_anchors) return null;

  const { degradation_start, collapse_point, recovery_detected } = timeline_anchors;

  const items = [
    { label: "Failure started",    value: degradation_start, urgent: true },
    { label: "Worst day",          value: collapse_point,    urgent: false },
    { label: "Recovery detected",  value: recovery_detected || "Not detected", urgent: false },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px,1fr))", gap: 12 }}>
      {items.map(({ label, value, urgent }) => (
        <div key={label} style={{
          padding: "10px 14px", borderRadius: "var(--radius-sm)",
          background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)",
        }}>
          <div style={{
            fontFamily: MONO, fontSize: 9, color: "var(--text-muted)",
            marginBottom: 4, letterSpacing: "0.1em", textTransform: "uppercase",
          }}>
            {label}
          </div>
          <div style={{
            fontFamily: MONO, fontSize: 13, fontWeight: 700,
            color: urgent && value ? "var(--danger)" : "var(--text-primary)",
          }}>
            {value || "—"}
          </div>
        </div>
      ))}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// ForensicHeader
// ─────────────────────────────────────────────────────────────

export function ForensicHeader({ result }) {
  if (!result?.forensic) return null;

  const { forensic, stats } = result;

  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 16,
      padding: "20px 24px", borderRadius: "var(--radius)",
      background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <ArchetypeBadge archetype={forensic.archetype} />
      </div>

      <TimelineAnchors timeline_anchors={forensic.timeline_anchors} />

      {stats?.estimated_loss > 0 && (
        <div>
          <div style={{
            fontFamily: MONO, fontSize: 9, color: "var(--text-muted)",
            marginBottom: 4, letterSpacing: "0.1em", textTransform: "uppercase",
          }}>
            Money lost
          </div>
          <div style={{ fontFamily: MONO, fontSize: 28, fontWeight: 700, color: "var(--danger)" }}>
            € {Math.round(stats.estimated_loss).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// IncidentBody — full forensic section
// ─────────────────────────────────────────────────────────────

export function IncidentBody({ result }) {
  if (!result?.forensic) return null;
  const { forensic } = result;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ForensicNarrative forensic={forensic} />
      <TriggerHypothesis trigger_hypothesis={forensic.trigger_hypothesis} />
      <WhatToCheck what_to_check={forensic.what_to_check} />
    </div>
  );
}
